import { useCallback, useMemo } from 'react';

import { useFormikContext } from 'formik';
import { FormPart } from '@modules/shared/components/FormPart';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { SSNInputField } from '@shared/fields/SSNInputField';
import { PhoneInputField } from '@shared/fields/PhoneInputField';
import { statesList } from '@shared/constants/statesList';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { MILITARY_RANK_OPTIONS, RESIDENT_TYPE_OPTIONS } from '@shared/constants/lists';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { MARITAL_STATUS_OPTIONS } from '@shared/lists';

import { Roles } from '@shared/constants/constants';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { SERVICES } from '@shared/constants/apiConfig';
import { ISearchInputFieldProps, SearchInputField } from '@shared/fields/SearchInputField';
import { IOption } from '@shared/interfaces';
import { NContact } from '../validation';
import { IReferralItemValue, SUFFIX_OPTIONS } from '../interfaces';

export const PersonalInfo = () => {
  const { values, setFieldValue } = useFormikContext<NContact.Entity>();
  const { isAdmin, userHasRoles } = useUserAccount();
  const isEditMLAEnable = isAdmin || userHasRoles([Roles.EDIT_MLA_FLAG]);
  const isEditOCPEnable = isAdmin || userHasRoles([Roles.EDIT_OCP_FLAG]);

  const handleReferalChange = useCallback((data?: IOption<unknown> | null) => {
    setFieldValue('referralContactId', data?.value);
    setFieldValue('referralContactName', data?.label);
  }, []);

  const config: IFormFieldConfig<NContact.Entity>[] = useMemo(
    () => [
      {
        name: 'firstName',
        width: '20%',
        minWidth: '200px',
      },
      {
        name: 'lastName',
        width: '20%',
        minWidth: '200px',
      },
      {
        name: 'middleInitial',
        label: 'Middle Name',
        width: '20%',
        minWidth: '120px',
      },
      {
        name: 'nickName',
        label: 'Nick Name',
        width: '12%',
        minWidth: '120px',
      },
      {
        name: 'suffix',
        width: '8%',
        minWidth: '80px',
        component: SelectAutocompleteField,
        options: SUFFIX_OPTIONS,
      },
      {
        name: 'dateOfBirth',
        label: 'DOB',
        width: '20%',
        minWidth: '200px',
        maxDate: new Date(),
        minDate: new Date('01-01-1900'),
        component: SelectDateField,
      },
      {
        name: 'ssn',
        label: 'SSN',
        width: '20%',
        minWidth: '200px',
        component: SSNInputField,
      },
      {
        name: 'email',
        label: 'Personal Email',
        width: '20%',
        minWidth: '200px',
      },
      {
        name: 'workEmail',
        width: '20%',
        minWidth: '200px',
      },
      {
        name: 'homePhone',
        width: '20%',
        minWidth: '200px',
        component: PhoneInputField,
      },
      {
        name: 'workPhone',
        width: '20%',
        minWidth: '200px',
        component: PhoneInputField,
      },
      {
        name: 'cellPhone',
        width: '20%',
        minWidth: '200px',
        component: PhoneInputField,
      },
      {
        name: 'maritalStatus',
        width: '20%',
        minWidth: '200px',
        component: SelectAutocompleteField,
        options: MARITAL_STATUS_OPTIONS,
      },
      {
        name: 'residencyStatusType',
        label: 'Residency',
        width: '20%',
        minWidth: '200px',
        component: SelectAutocompleteField,
        options: RESIDENT_TYPE_OPTIONS,
      },
      {
        name: 'maidenName',
        label: 'Mothers Maiden',
        width: '20%',
        minWidth: '200px',
      },
      {
        name: 'militaryStatus',
        label: 'Military Rank',
        width: '20%',
        minWidth: '200px',
        component: SelectAutocompleteField,
        options: MILITARY_RANK_OPTIONS,
      },
      {
        name: 'referralContactId',
        label: 'Referral',
        width: '20%',
        minWidth: '200px',
        component: (props: JSX.IntrinsicAttributes & ISearchInputFieldProps<IReferralItemValue>) => <SearchInputField<IReferralItemValue, string>
          {...props}
          label="Referral"
          fetchUrl={`${SERVICES.CONTACT}/contacts/names`}
          defaultValue={{
            label: values.referralContactName || '',
            value: values.referralContactId || ''
          }}
          mapOptionsFn={(option) => ({
            label: `${option.firstName} ${option.lastName}`,
            value: option.contactId,
          })} 
          params={{
            referralContactId: values.referralContactId || '',
          }}
          onChange={(_value, fullValueObj) => handleReferalChange(fullValueObj)}
        />,
      },
      {
        name: 'driverLicenseNumber',
        label: 'DL Number',
        width: '20%',
        minWidth: '200px',
      },
      {
        name: 'driverLicenseState',
        label: 'DL Issue State',
        width: '20%',
        minWidth: '200px',
        options: statesList,
        component: SelectAutocompleteField,
      },
      {
        name: 'driverLicenseIssuesOnDate',
        label: 'DL Issue Date',
        width: '20%',
        minWidth: '200px',
        maxDate: new Date(),
        component: SelectDateField,
      },
      {
        name: 'driverLicenseExpireDate',
        label: 'DL Expired Date',
        width: '20%',
        minWidth: '200px',
        component: SelectDateField,
      },
      {
        name: 'referralEmail',
        label: 'Referral Email',
        width: '20%',
        minWidth: '200px',
      },
      {
        name: 'isSpanish',
        label: 'Spanish',
        width: '12%',
        minWidth: '100px',
        component: CheckboxField,
      },
      {
        name: 'isDeceased',
        label: 'Deceased',
        width: '12%',
        minWidth: '100px',
        component: CheckboxField,
      },
      {
        name: 'isMilitaryLendingActAvailable',
        label: 'Military Lending Act',
        width: '17%',
        minWidth: '100px',
        disabled: !isEditMLAEnable,
        component: CheckboxField,
      },
      {
        name: 'oneCreditPull',
        label: 'One Credit Pull',
        width: '15%',
        minWidth: '100px',
        disabled: !isEditOCPEnable,
        component: CheckboxField,
      },
    ],
    [
      values.referralContactName,
      values.referralContactId,
      isEditMLAEnable,
      isEditOCPEnable
    ]
  );

  return (
    <FormPart
      name="Personal Information"
      config={config}
      marginBottom={2}
      marginTop={1}
    />
  );
};
